import React from 'react';
import type {
  QuestionType,
  BlankSlot,
  ReorderItem,
  ReadingQuestion,
  PassagePage,
} from '@examing/types';
import { FillInBlanksEditor } from './FillInBlanksEditor';
import { ReorderEditor } from './ReorderEditor';
import { WordLimitField } from './ShortAnswerFields';
import { PassageEditor } from './PassageEditor';

/** The exact shape stored on BankQuestion / Question and read by the backend grader. */
export interface QuestionTypeData {
  matchingOptions?: string[];
  answerMode?: 'exact' | 'manual';
  acceptedAnswers?: string[];
  maxWords?: number | null;
  blanks?: BlankSlot[];
  reorderItems?: ReorderItem[];
  correctOrder?: string[];
  mediaUrl?: string;
  /** What the picture shows, for the AI answer analysis only. Never shown to a candidate. */
  aiImageDescription?: string;
  rubric?: string;

  // PASSAGE
  passageText?: string;
  passageTitle?: string;
  subQuestions?: ReadingQuestion[];
  pages?: PassagePage[];
}

/** Question types whose authoring UI is provided by this dispatcher. */
export const DISPATCHED_QUESTION_TYPES: QuestionType[] = [
  'FILL_IN_BLANKS',
  'OPEN_ENDED',
  'REORDER',
  'PASSAGE',
];

interface Props {
  type: QuestionType;
  /** The question prompt — some editors (blanks/hot-text) own it. */
  prompt: string;
  onPromptChange: (prompt: string) => void;
  data: QuestionTypeData;
  onChange: (patch: Partial<QuestionTypeData>) => void;
}

/** Returns null for types it doesn't own — basic/choice types are handled by the parent form. */
export const QuestionTypeEditor: React.FC<Props> = ({
  type,
  prompt,
  onPromptChange,
  data,
  onChange,
}) => {
  switch (type) {
    case 'FILL_IN_BLANKS':
      return (
        <><FillInBlanksEditor
          prompt={prompt}
          blanks={(data.blanks ?? []).map((b) => ({
            ...b,
            acceptedAnswers: b.acceptedAnswers ?? [],
          }))}
          onChange={({ prompt: nextPrompt, blanks }) => { onPromptChange(nextPrompt); onChange({ blanks }); }}
        /><WordLimitField value={data.maxWords} onChange={(maxWords) => onChange({ maxWords })} /></>
      );

    case 'OPEN_ENDED':
      return (
        <div className="space-y-2">
          <label className="text-sm font-medium block">Grading rubric (optional)</label>
          <textarea
            className="w-full min-h-[90px] rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="Describe what a correct answer should contain. Open-ended answers are graded manually or by AI."
            value={data.rubric ?? ''}
            onChange={(e) => onChange({ rubric: e.target.value })}
          />
        </div>
      );

    case 'REORDER':
      return (
        <ReorderEditor
          items={data.reorderItems ?? []}
          correctOrder={data.correctOrder ?? []}
          onChange={({ items: reorderItems, correctOrder }) => onChange({ reorderItems, correctOrder })}
        />
      );

    case 'PASSAGE':
      // The passage text doubles as the question prompt (required, schema-side),
      // so keep both in sync. Sub-questions persist as loose objects.
      return (
        <PassageEditor
          passageText={data.passageText ?? prompt ?? ''}
          passageTitle={data.passageTitle}
          subQuestions={data.subQuestions ?? []}
          pages={data.pages}
          onPassageTextChange={(passageText) => {
            onChange({ passageText });
            onPromptChange(passageText);
          }}
          onPassageTitleChange={(passageTitle) => onChange({ passageTitle })}
          onSubQuestionsChange={(subQuestions) =>
            onChange({ subQuestions })
          }
          onPagesChange={(pages, consolidatedSubQuestions, mainPassageText, mainPassageTitle) => {
            onChange({
              pages,
              subQuestions: consolidatedSubQuestions,
              passageText: mainPassageText,
              passageTitle: mainPassageTitle,
            });
            if (mainPassageText) {
              onPromptChange(mainPassageText);
            }
          }}
        />
      );

    default:
      return null;
  }
};
